import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { FetchAssetParams, Schedule, ScheduleCreate } from '../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { StoreModel } from '../..';

export interface ScheduleModel {
  fetchSchedule: Thunk<ScheduleModel, string, Injections, Promise<Schedule>>;
  fetchSchedules: Thunk<ScheduleModel, FetchAssetParams, Injections, Promise<Schedule[]>>;
  createSchedule: Thunk<ScheduleModel, ScheduleCreate, Injections, StoreModel, Promise<Schedule>>;
  scheduleActionStart: Action<ScheduleModel, void>;
  lastAction?: number;
  schedules: Record<string, Schedule>;
  scheduleFailure: Action<ScheduleModel, ApplicationErrorOptions>;
  setSchedules: Action<ScheduleModel, Schedule[]>;
}
export const scheduleModel: ScheduleModel = {
  schedules: {},
  scheduleFailure: action((state, payload) => {
    console.error(payload);
  }),
  setSchedules: action((state, payload) => {
    for (const val of payload) {
      state.schedules[val.scheduleId] = val;
    }
  }),
  scheduleActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  fetchSchedule: thunk(async (actions, payload, { injections }) => {
    try {
      actions.scheduleActionStart();
      const res = await injections.assetService.fetchSchedule(payload);
      actions.setSchedules([res]);
      return res;
    } catch (error) {
      actions.scheduleFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching schedule.',
        asNotification: true,
      });
    }
  }),
  fetchSchedules: thunk(async (actions, payload, { injections }) => {
    try {
      actions.scheduleActionStart();
      const res = await injections.assetService.fetchSchedules(payload);
      actions.setSchedules(res);
      return res;
    } catch (error) {
      actions.scheduleFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching schedule.',
        asNotification: true,
      });
    }
  }),
  createSchedule: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.scheduleActionStart();
      const res = await injections.assetService.createSchedule(payload);
      actions.setSchedules([res]);

      // Invalidate schedule queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'schedule-create',
        entityId: res.scheduleId,
      });

      return res;
    } catch (error) {
      actions.scheduleFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating schedules.',
        asNotification: true,
      });
      return error;
    }
  }),
};
